/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Plus, Boxes, Trash2, Check, X, Loader2, Edit2, FileSpreadsheet, Download, ChevronDown, Filter, Search, ArrowLeft, Package, ArrowRightLeft, Wine, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useCollections } from "@/context/CollectionContext";
import { useTier } from "@/context/AuthContext";
import EmptyState from "@/components/EmptyState";
import { SkeletonList } from "@/components/Skeleton";
import CategoryPill from "@/components/CategoryPill";
import BottomNav from "@/components/BottomNav";
import InferredValueIndicator from "@/components/InferredValueIndicator";
import ComboBox from "@/components/ComboBox";
import MoveItemsModal from "@/components/MoveItemsModal";
import DuplicateCleanupModal from "@/components/DuplicateCleanupModal";
import SwipeableRow from "@/components/SwipeableRow";  // retained for potential reuse; swipe-to-delete intentionally disabled per UX request
import { resolveBackHref } from "@/lib/navTracker";
import { itemCost, itemValue, itemCostNZD, itemValueNZD, isValueInferred } from "@/utils/itemValue";
import { fmt2 } from "@/lib/money";
import { getCleanupHistoryCap } from "@/lib/cleanupHistory";
import { isSampleCollectionId } from "@/lib/sampleImport";
import { linkSafeSelectHandlers } from "@/lib/dropdownHandlers";
import { addCleanupEvent } from "@/lib/cleanupDigest";
import { composeAutoName, composeIdentityDots, isNameOverride } from "@/lib/composeItemName";

const fmtPrice = (n) => `$${fmt2(n)}`;

/**
 * Items — Step 4 rebuild.
 *  - List of all items (name + category pill).
 *  - "Create Item" button → /items/new
 *  - Delete per row.
 *  - BottomNav to Collections + Categories.
 *
 * No filters, no edit, no collection column.
 */
export default function Items() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  // Region / Producer / Grape / Sub-Region / Bottle Size preset values — drive
  // the filter ComboBoxes. Pulled from /api/presets on mount and refreshed
  // after any data change.
  const [filterPresets, setFilterPresets] = useState({
    Region: [],
    Producer: [],
    "Grape(s)": [],
    "Sub-Region": [],
    "Bottle Size": [],
    // Whisky
    Distillery: [],
    "Cask Type": [],
    // Spirits
    Type: [],
    Country: [],
    // General Collectables
    Category: [],
    "Sub-Category": [],
    Condition: [],
    "Grade / Certification": [],
    // Custom
    "Primary Identifier": [],
    "Secondary Identifier": [],
    "Condition / Grade": [],
  });
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Local UI state for the collapsible Filters panel.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Track viewport width so the per-row "Change category" dropdown can
  // show the full label on desktop and a shorter fallback on mobile
  // (where the pill-column collapses to ~135 px and would clip the
  // longer text). Threshold matches Tailwind's `sm` breakpoint (640 px).
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 639px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Inline rename — renames item_data["Item Name"] in place.
  const [renameId, setRenameId] = useState(null);
  const [renameName, setRenameName] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const { collections } = useCollections();
  const { canCsvExport } = useTier();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter values are derived from URL params (single source of truth) so
  // both inline navigation (e.g. clicking an Uncategorised pill) and dropdown
  // changes update the same place. No useState + useEffect sync needed.
  const filterCollectionId = searchParams.get("collection_id") || "";
  const filterCategoryId = searchParams.get("category_id") || "";
  const filterRegion   = searchParams.get("region")   || "";
  const filterProducer = searchParams.get("producer") || "";
  const filterGrape    = searchParams.get("grape")    || "";
  // New Wine-specific filters (added with the Search + Filters panel).
  const filterSubRegion  = searchParams.get("sub_region")  || "";
  const filterBottleSize = searchParams.get("bottle_size") || "";
  const filterVintageMin = searchParams.get("vmin") || "";
  const filterVintageMax = searchParams.get("vmax") || "";
  const filterDistYearMin = searchParams.get("dymin") || "";
  const filterDistYearMax = searchParams.get("dymax") || "";
  const filterRatingMin  = searchParams.get("rmin") || "";
  const filterRatingMax  = searchParams.get("rmax") || "";
  const filterDwStartMin = searchParams.get("dwsmin") || "";
  const filterDwStartMax = searchParams.get("dwsmax") || "";
  const filterDwPeakMin  = searchParams.get("dwpmin") || "";
  const filterDwPeakMax  = searchParams.get("dwpmax") || "";
  const filterDwEndMin   = searchParams.get("dwemin") || "";
  const filterDwEndMax   = searchParams.get("dwemax") || "";
  // Purchase-date upper bound (YYYY-MM or YYYY-MM-DD). Drives the deep-link
  // from the Dashboard's "P/L Over Time" line chart so a point click filters
  // to all items purchased on/before that month.
  const filterPurchasedBefore = searchParams.get("purchased_before") || "";
  // Wine-type filter (case-insensitive). Drives the deep-link from the
  // Dashboard's "Cellar Composition" donut. Matches item.wine_type (top-
  // level) OR item_data["Wine Type"] / ["Type"]. Treats Rosé/Rose as the
  // same value so URL-safe strings work transparently.
  const filterWineType = searchParams.get("wine_type") || "";
  // Type-aware filter params — Whisky / Spirits / General / Custom.
  // Each maps 1:1 to a master field name so the filter logic below
  // can drive item_data lookups directly.
  const filterDistillery   = searchParams.get("distillery")   || "";
  const filterCaskType     = searchParams.get("cask_type")    || "";
  const filterAgeMin       = searchParams.get("age_min")      || "";
  const filterAgeMax       = searchParams.get("age_max")      || "";
  const filterAbvMin       = searchParams.get("abv_min")      || "";
  const filterAbvMax       = searchParams.get("abv_max")      || "";
  const filterSpiritType   = searchParams.get("spirit_type")  || ""; // Spirits "Type"
  const filterCountry      = searchParams.get("country")      || "";
  const filterGcCategory   = searchParams.get("gc_category")  || "";
  const filterCondition    = searchParams.get("condition")    || "";
  const filterGrade        = searchParams.get("grade")        || "";
  const filterPrimaryId    = searchParams.get("primary_id")   || "";
  const filterSecondaryId  = searchParams.get("secondary_id") || "";
  const filterConditionGrade = searchParams.get("condition_grade") || "";
  const searchQuery      = searchParams.get("q") || "";

  // Local input state for the search box — decouples the visible input
  // from the URL/filter state so we can debounce the URL write (200 ms)
  // without throttling the keystroke feedback. The filter logic still
  // reads `searchQuery` from the URL, so the user gets:
  //   - instant character-by-character input feedback,
  //   - 200 ms debounced filter application (no stale-result flicker),
  //   - clearing the input → debounce fires → URL `q` removed → full list.
  const [searchInput, setSearchInput] = useState(searchQuery);
  // Keep local input in sync if the URL is changed externally
  // (clearFilters / back nav / shared link).
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);
  // 200 ms debounce: only push to the URL when the user stops typing.
  useEffect(() => {
    if (searchInput === searchQuery) return;
    const t = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 200);
    return () => clearTimeout(t);
  }, [searchInput, searchQuery]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const setFilterCollectionId = (v) => setFilter("collection_id", v);
  const setFilterCategoryId = (v) => setFilter("category_id", v);
  const setFilterRegion     = (v) => setFilter("region", v);
  const setFilterProducer   = (v) => setFilter("producer", v);
  const setFilterGrape      = (v) => setFilter("grape", v);
  const setFilterSubRegion  = (v) => setFilter("sub_region", v);
  const setFilterBottleSize = (v) => setFilter("bottle_size", v);
  const setFilterDistillery = (v) => setFilter("distillery", v);
  const setFilterCaskType   = (v) => setFilter("cask_type", v);
  const setFilterSpiritType = (v) => setFilter("spirit_type", v);
  const setFilterCountry    = (v) => setFilter("country", v);
  const setFilterGcCategory = (v) => setFilter("gc_category", v);
  const setFilterCondition  = (v) => setFilter("condition", v);
  const setFilterGrade      = (v) => setFilter("grade", v);
  const setFilterPrimaryId  = (v) => setFilter("primary_id", v);
  const setFilterSecondaryId = (v) => setFilter("secondary_id", v);
  const setFilterConditionGrade = (v) => setFilter("condition_grade", v);
  const setSearchQuery      = (v) => setFilter("q", v);
  const clearFilters = () => {
    // Preserve only the sort param when clearing — every other URL filter
    // (including the search box) is reset. URL is the canonical source of
    // truth for every filter value (each `filterX` const reads from
    // `searchParams.get(...)`) so wiping the URL implicitly resets every
    // controlled filter input on the next render. `replace: true` ensures
    // the cleared state replaces the previous filtered URL in history, so
    // the Back button cannot restore old filters.
    const next = new URLSearchParams();
    const sort = searchParams.get("sort");
    if (sort) next.set("sort", sort);
    setSearchParams(next, { replace: true });
    // Defense-in-depth: explicitly reset the local debounced search input
    // immediately, so the visible textbox can't briefly render its old
    // value while React is still flushing the URL → state sync useEffect
    // (and so any in-flight 200 ms debounce write can't re-add a stale
    // `q` param after the clear). The `searchQuery → searchInput` effect
    // would do this anyway one render later; this just removes the frame
    // of stale UI and the window for a debounce race.
    setSearchInput("");
  };

  // True when at least one filter or the search box is active. Used to
  // gate the "Clear all" button and the active-count badge on the toggle.
  const activeFilterKeys = [
    filterCollectionId, filterCategoryId, filterRegion, filterProducer,
    filterGrape, filterSubRegion, filterBottleSize,
    filterVintageMin, filterVintageMax, filterDistYearMin, filterDistYearMax,
    filterRatingMin, filterRatingMax,
    filterDwStartMin, filterDwStartMax, filterDwPeakMin, filterDwPeakMax,
    filterDwEndMin, filterDwEndMax, filterPurchasedBefore, filterWineType, searchQuery,
    // Type-aware additions
    filterDistillery, filterCaskType, filterAgeMin, filterAgeMax,
    filterAbvMin, filterAbvMax, filterSpiritType, filterCountry,
    filterGcCategory, filterCondition, filterGrade,
    filterPrimaryId, filterSecondaryId, filterConditionGrade,
  ];
  const activeFilterCount = activeFilterKeys.filter((v) => v !== "").length;

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        apiClient.get("/items"),
        apiClient.get("/categories"),
      ]);
      setItems(a.data || []);
      setCategories(b.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, b] = await Promise.all([
          apiClient.get("/items"),
          apiClient.get("/categories"),
        ]);
        if (cancelled) return;
        setItems(a.data || []);
        setCategories(b.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load preset values used by the filter ComboBoxes. Refreshed whenever
  // the items list changes so newly-typed values surface immediately.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fieldNames = [
          "Region","Producer","Grape(s)","Sub-Region","Bottle Size",
          "Distillery","Cask Type","Type","Country",
          "Category","Sub-Category","Condition","Grade / Certification",
          "Primary Identifier","Secondary Identifier","Condition / Grade",
        ].join(",");
        const { data } = await apiClient.get(`/presets?field_names=${encodeURIComponent(fieldNames)}`);
        if (!cancelled) setFilterPresets({
          Region:        data?.Region        || [],
          Producer:      data?.Producer      || [],
          "Grape(s)":    data?.["Grape(s)"]  || [],
          "Sub-Region":  data?.["Sub-Region"] || [],
          "Bottle Size": data?.["Bottle Size"] || [],
          Distillery:    data?.Distillery    || [],
          "Cask Type":   data?.["Cask Type"] || [],
          Type:          data?.Type          || [],
          Country:       data?.Country       || [],
          Category:      data?.Category      || [],
          "Sub-Category": data?.["Sub-Category"] || [],
          Condition:     data?.Condition     || [],
          "Grade / Certification": data?.["Grade / Certification"] || [],
          "Primary Identifier":   data?.["Primary Identifier"]   || [],
          "Secondary Identifier": data?.["Secondary Identifier"] || [],
          "Condition / Grade":    data?.["Condition / Grade"]    || [],
        });
      } catch { /* tolerate */ }
    })();
    return () => { cancelled = true; };
  }, [items]);

  const catObj = (cid) => categories.find((c) => c.id === cid) || null;
  const collectionName = (cid) => collections.find((c) => c.id === cid)?.name || "—";

  // ------------------------------------------------------------------
  // Bottle Size filter options — unified, complete, deterministic list.
  // Built from THREE sources merged together:
  //   1. Canonical preset sizes (CDI defaults — always shown so the
  //      filter is never empty on a fresh / empty dataset).
  //   2. Server-known preset values (filterPresets["Bottle Size"] — any
  //      value typed by ANY user, persisted via the /presets endpoint).
  //   3. Actual values present on the full `items` cache (loaded once on
  //      mount via GET /items — NOT the filtered/sorted view). Guarantees
  //      that every size physically in the database is selectable.
  // Sorted numerically (after normalising ml/L) so 187ml < 375ml < 500ml
  // < 750ml < 1L < 1.5L < 3L < 6L, with any non-numeric stragglers
  // (e.g. "Magnum") appended in lexicographic order at the end.
  // ------------------------------------------------------------------
  const BOTTLE_SIZE_PRESETS = ["187ml", "375ml", "500ml", "750ml", "1L", "1.5L", "3L", "6L"];
  const sizeToMl = (s) => {
    const m = String(s || "").trim().toLowerCase().match(/^([\d.]+)\s*(ml|l)$/);
    if (!m) return NaN;
    const n = parseFloat(m[1]);
    if (!Number.isFinite(n)) return NaN;
    return m[2] === "l" ? n * 1000 : n;
  };
  const bottleSizeOptions = useMemo(() => {
    const set = new Set();
    BOTTLE_SIZE_PRESETS.forEach((s) => set.add(s));
    (filterPresets["Bottle Size"] || []).forEach((s) => {
      const t = String(s || "").trim();
      if (t) set.add(t);
    });
    for (const it of items) {
      const v = String(it?.item_data?.["Bottle Size"] || "").trim();
      if (v) set.add(v);
    }
    const arr = Array.from(set);
    arr.sort((a, b) => {
      const am = sizeToMl(a);
      const bm = sizeToMl(b);
      const aN = Number.isFinite(am);
      const bN = Number.isFinite(bm);
      if (aN && bN) return am - bm;
      if (aN) return -1;
      if (bN) return 1;
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
    return arr;
  }, [filterPresets, items]);

  // ------------------------------------------------------------------
  // Grape filter options — Wine-scoped, flattened, deduped.
  // Built ONLY from items in Wine-type collections so Spirits / Whisky /
  // other collection grape-ish values never leak in.
  // Handles the three real-world storage shapes seen in the dataset:
  //   1. array of strings   → ["Merlot", "Cabernet Sauvignon"]
  //   2. array with comma   → ["Cabernet Sauvignon, Merlot, Petit Verdot"]
  //   3. comma-joined string → "Cabernet Sauvignon, Merlot, Petit Verdot"
  // Each entry is split on commas so multi-grape blends surface every
  // individual grape as its own option (fixes the "shows 2, should show 3"
  // bug where the joined string counted as a single option).
  // Sorted alphabetically (case-insensitive) for deterministic order.
  // Isolated to this page — does not touch /api/presets or global filters.
  // ------------------------------------------------------------------
  const grapeOptions = useMemo(() => {
    const wineCollectionIds = new Set(
      (collections || [])
        .filter((c) => (c?.type_id || "").toLowerCase() === "wine")
        .map((c) => c.id)
    );
    const set = new Set();
    for (const it of items) {
      if (!wineCollectionIds.has(it.collection_id)) continue;
      const raw = (it.item_data || {})["Grape(s)"];
      const entries = Array.isArray(raw)
        ? raw
        : (raw === undefined || raw === null || raw === "" ? [] : [raw]);
      for (const entry of entries) {
        String(entry)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((g) => set.add(g));
      }
    }
    return Array.from(set).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  }, [items, collections]);

  // Grape-specific matcher — comma-aware so a selected grape ("Merlot")
  // matches items whose Grape(s) is stored as an array, a joined string,
  // or a mix. Kept separate from `matchItemDataField` so no other filter
  // behaviour changes.
  const matchGrapeFilter = (it, want) => {
    if (!want) return true;
    const raw = (it.item_data || {})["Grape(s)"];
    const entries = Array.isArray(raw)
      ? raw
      : (raw === undefined || raw === null || raw === "" ? [] : [raw]);
    const needle = String(want).trim().toLowerCase();
    for (const entry of entries) {
      const parts = String(entry).split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
      if (parts.includes(needle)) return true;
    }
    return false;
  };

  // Resolve the CDI master-type key ("wine" / "whisky" / "spirits" /
  // "general" / "custom" / "") for the currently selected collection —
  // drives which locked filter group renders below. Falls back to name
  // regex when type_id is unset for legacy rows.
  const activeCollectionType = useMemo(() => {
    if (!filterCollectionId) return "";
    const c = collections.find((x) => x.id === filterCollectionId);
    if (!c) return "";
    const tid = String(c.type_id || "").toLowerCase();
    if (["wine","whisky","spirits","general","custom"].includes(tid)) return tid;
    const n = String(c.name || "").toLowerCase();
    if (/whisk/.test(n)) return "whisky";
    if (/wine/.test(n)) return "wine";
    if (/spirit|rum|gin|vodka|tequila|cognac|mezcal|brandy|liqueur/.test(n)) return "spirits";
    if (/general|collectable|card|coin|comic|memorabilia|art/.test(n)) return "general";
    if (/custom/.test(n)) return "custom";
    return "";
  }, [filterCollectionId, collections]);

  // Distinct list of existing item names, sorted alphabetically
  // (case-insensitive). Powers the autosuggest dropdown on the search bar
  // — top 10 substring matches surface as the user types.
  const itemNameOptions = (() => {
    const seen = new Map();
    for (const it of items) {
      const candidates = [it?.item_name, it?.item_data?.["Item Name"]];
      for (const raw of candidates) {
        if (!raw) continue;
        const name = String(raw).trim();
        if (!name) continue;
        const key = name.toLowerCase();
        if (!seen.has(key)) seen.set(key, name);
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  })();

  // Categories available in the category dropdown depend on the selected collection.
  const filteredCategoryOptions = filterCollectionId
    ? categories.filter((c) => c.collection_id === filterCollectionId)
    : categories;

  // If the chosen category filter doesn't belong to the chosen collection,
  // ignore it (don't apply it). User can also click Clear.
  // Sentinel value "__UNCAT__" represents "items without a category".
  const UNCAT = "__UNCAT__";
  const effectiveCategoryId =
    filterCategoryId === UNCAT
      ? UNCAT
      : filterCategoryId &&
        (!filterCollectionId || catObj(filterCategoryId)?.collection_id === filterCollectionId)
        ? filterCategoryId
        : "";

  // Items visible = items matching every active filter.  Each item_data filter
  // matches case-insensitively against the field value (or any list entry for
  // multi-value fields like Grape(s)).
  const matchItemDataField = (it, fieldName, want) => {
    if (!want) return true;
    const v = (it.item_data || {})[fieldName];
    if (Array.isArray(v)) return v.map((x) => String(x).toLowerCase()).includes(want.toLowerCase());
    return String(v || "").toLowerCase() === want.toLowerCase();
  };
  // Numeric-range filter: returns true when `val` falls inside the optional
  // [min, max] bounds. Empty bounds are wildcards; non-numeric values fail.
  const matchNumberRange = (val, min, max) => {
    if (!min && !max) return true;
    if (val === undefined || val === null || val === "") return false;
    // Parse first numeric run — accepts values like "12", "12 years",
    // "43%", "43.5". Falls back to Number() if the regex misses.
    const m = String(val).match(/-?\d+(?:\.\d+)?/);
    const n = m ? Number(m[0]) : Number(val);
    if (Number.isNaN(n)) return false;
    if (min !== "" && n < Number(min)) return false;
    if (max !== "" && n > Number(max)) return false;
    return true;
  };
  // Free-text search across the listed fields plus Category and Collection
  // names (looked up from the loaded categories/collections lists since these
  // aren't stored in item_data). Matches as a case-insensitive substring on
  // the stringified value. For array fields (e.g. Grape(s) stored as a list),
  // every entry is concatenated then matched.
  //
  // Field list covers every primary identifier across the CDI industry-
  // standard master templates (Wine / Whisky / Spirits / General
  // Collectables / Custom) so free-text search picks up matches regardless
  // of collection type. Legacy Whisky/Spirits keys are included so items
  // created before the rename still surface via search.
  const SEARCH_FIELDS = [
    // Wine
    "Producer", "Item Name", "Vintage", "Region", "Sub-Region",
    "Appellation", "Grape(s)", "Classification",
    // Whisky (CDI locked — legacy keys in fallback only)
    "Distillery", "Age", "Cask Type", "Bottling Year", "Release Type",
    // Spirits (CDI locked — legacy keys in fallback only)
    "Producer / Distillery", "Type",
    // General Collectables
    "Category", "Sub-Category", "Year", "Condition", "Grade / Certification",
    // Custom
    "Primary Identifier", "Secondary Identifier", "Year / Origin",
    "Condition / Grade",
    // Shared / notes + legacy fallback keys (still searched but no
    // longer part of any CDI primary block — retained for pre-rename
    // items so free-text still surfaces them).
    "Notes", "Tasting Notes", "Personal Notes",
    "Age Statement", "Batch / Release", "Whisky Name", "Series",
    "Brand / Producer", "Spirit Type",
  ];
  const matchSearch = (it, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    const d = it.item_data || {};
    // 1. item_data fields
    const inItemData = SEARCH_FIELDS.some((f) => {
      const v = d[f];
      if (Array.isArray(v)) return v.some((x) => String(x).toLowerCase().includes(needle));
      return String(v ?? "").toLowerCase().includes(needle);
    });
    if (inItemData) return true;
    // 2. Category name (via category_id lookup)
    const catName = catObj(it.category_id)?.category_name || "";
    if (catName && catName.toLowerCase().includes(needle)) return true;
    // 3. Collection name (via collection_id lookup). Only contributes when
    //    there's at least one collection loaded — keeps behaviour clean when
    //    collections are disabled / not yet set up.
    if (collections.length > 0) {
      const collName = collectionName(it.collection_id) || "";
      if (collName && collName !== "—" && collName.toLowerCase().includes(needle)) return true;
    }
    return false;
  };
  // CDI Master Rule — hide items whose parent collection is a Premium
  // collection (or has been premium-locked). The `collections` state
  // already comes from the tier-filtered CollectionContext, so any
  // collection_id NOT in that visible set belongs to a hidden Premium
  // collection and its items must not surface either. Admins bypass
  // because the context returns the full list to them.
  const visibleCollectionIds = useMemo(
    () => new Set(collections.map((c) => c.id)),
    [collections]
  );
  const filteredItems = items.filter((it) => {
    if (it.collection_id && !visibleCollectionIds.has(it.collection_id)) return false;
    if (filterCollectionId && it.collection_id !== filterCollectionId) return false;
    if (effectiveCategoryId === UNCAT) {
      if (it.category_id) return false;
    } else if (effectiveCategoryId) {
      if (it.category_id !== effectiveCategoryId) return false;
    }
    if (!matchItemDataField(it, "Region",      filterRegion))     return false;
    if (!matchItemDataField(it, "Producer",    filterProducer))   return false;
    if (!matchGrapeFilter(it, filterGrape))                       return false;
    if (!matchItemDataField(it, "Sub-Region",  filterSubRegion))  return false;
    if (!matchItemDataField(it, "Bottle Size", filterBottleSize)) return false;
    // Type-aware filters — Whisky / Spirits / General / Custom.
    if (!matchItemDataField(it, "Distillery",  filterDistillery)) return false;
    if (!matchItemDataField(it, "Cask Type",   filterCaskType))   return false;
    if (!matchItemDataField(it, "Type",        filterSpiritType)) return false;
    if (!matchItemDataField(it, "Country",     filterCountry))    return false;
    if (!matchItemDataField(it, "Category",    filterGcCategory)) return false;
    if (!matchItemDataField(it, "Condition",   filterCondition))  return false;
    if (!matchItemDataField(it, "Grade / Certification", filterGrade)) return false;
    if (!matchItemDataField(it, "Primary Identifier",   filterPrimaryId))   return false;
    if (!matchItemDataField(it, "Secondary Identifier", filterSecondaryId)) return false;
    if (!matchItemDataField(it, "Condition / Grade",    filterConditionGrade)) return false;
    const d = it.item_data || {};
    if (!matchNumberRange(d["Vintage"],               filterVintageMin, filterVintageMax)) return false;
    if (!matchNumberRange(d["Distillation Year"],     filterDistYearMin, filterDistYearMax)) return false;
    if (!matchNumberRange(d["Rating"],                filterRatingMin,  filterRatingMax))  return false;
    if (!matchNumberRange(d["Drinking Window Start"], filterDwStartMin, filterDwStartMax)) return false;
    if (!matchNumberRange(d["Drinking Window Peak"],  filterDwPeakMin,  filterDwPeakMax))  return false;
    if (!matchNumberRange(d["Drinking Window End"],   filterDwEndMin,   filterDwEndMax))   return false;
    // Numeric range on Age / ABV — reads the item_data value (may be a
    // string with units like "12 years" or "43%"; matchNumberRange strips
    // non-numeric characters before comparing).
    if (!matchNumberRange(d["Age"] || d["Age Statement"], filterAgeMin, filterAgeMax)) return false;
    if (!matchNumberRange(d["ABV"] || d["ABV %"],         filterAbvMin, filterAbvMax)) return false;
    // Purchased-before filter: keep items whose purchase month ≤ the
    // YYYY-MM (or YYYY-MM-DD) bound. Items with no purchase_date are
    // excluded (they can't satisfy a date constraint).
    if (filterPurchasedBefore) {
      const raw = it.purchase_date || (it.item_data || {})["Purchase Date"];
      if (!raw) return false;
      const itemMonth = String(raw).slice(0, 7); // "YYYY-MM"
      const boundMonth = String(filterPurchasedBefore).slice(0, 7);
      if (itemMonth.length < 7 || boundMonth.length < 7) return false;
      if (itemMonth > boundMonth) return false;
    }
    // Wine-type filter: match against item.wine_type (top-level), or the
    // legacy item_data["Wine Type"] / ["Type"] dynamic fields. Comparison
    // is case-insensitive with diacritics-normalised so "Rose" and "Rosé"
    // unify. Items missing all three fields are excluded.
    if (filterWineType) {
      const candidates = [
        it.wine_type,
        (it.item_data || {})["Wine Type"],
        (it.item_data || {})["Type"],
      ].map((v) => (v ? String(v).trim() : ""));
      const norm = (s) => s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
      const want = norm(filterWineType);
      if (!candidates.some((c) => c && norm(c) === want)) return false;
    }
    if (!matchSearch(it, searchQuery)) return false;
    return true;
  });

  // ---- Sorting (filter + sort work together) -------------------------
  const sortMode = searchParams.get("sort") || (() => {
    try { return localStorage.getItem("cdi.items.sort") || "date-desc"; }
    catch { return "date-desc"; }
  })();
  const setSortMode = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v && v !== "date-desc") next.set("sort", v); else next.delete("sort");
    setSearchParams(next, { replace: true });
    try { localStorage.setItem("cdi.items.sort", v || "date-desc"); } catch (_e) { /* noop */ }
  };
  const itemTitleSort = (it) =>
    ((it.item_data && it.item_data["Item Name"]) || it.name || it.item_name || "").toLowerCase();
  const itemVintage = (it) => {
    const v = (it.item_data && (it.item_data["Vintage"] ?? it.item_data["vintage"])) ?? it.vintage;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  // For sorts: items without a vintage sink to the bottom regardless of order.
  const vintageAsc = (a, b) => {
    const av = itemVintage(a); const bv = itemVintage(b);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return av - bv;
  };
  const vintageDesc = (a, b) => {
    const av = itemVintage(a); const bv = itemVintage(b);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return bv - av;
  };
  // Numeric epoch helper for Date Added sort. The canonical field on items
  // is `date_added` (ISO 8601). We keep `created_at` as a secondary lookup
  // for legacy documents only — no new field is introduced. Items with no
  // parseable date sink to the bottom regardless of sort direction (same
  // null-handling pattern as the vintage sort).
  const itemDateMs = (it) => {
    const d = it.date_added || it.created_at;
    const t = d ? Date.parse(d) : NaN;
    return Number.isFinite(t) ? t : null;
  };
  const dateAsc = (a, b) => {
    const av = itemDateMs(a); const bv = itemDateMs(b);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return av - bv;
  };
  const dateDesc = (a, b) => {
    const av = itemDateMs(a); const bv = itemDateMs(b);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return bv - av;
  };
  const sortFns = {
    "name-asc":  (a, b) => itemTitleSort(a).localeCompare(itemTitleSort(b)),
    "name-desc": (a, b) => itemTitleSort(b).localeCompare(itemTitleSort(a)),
    "price-asc":  (a, b) => itemCostNZD(a) - itemCostNZD(b),
    "price-desc": (a, b) => itemCostNZD(b) - itemCostNZD(a),
    "value-asc":  (a, b) => itemValueNZD(a) - itemValueNZD(b),
    "value-desc": (a, b) => itemValueNZD(b) - itemValueNZD(a),
    "date-asc":  dateAsc,
    "date-desc": dateDesc,
    "vintage-asc":  vintageAsc,
    "vintage-desc": vintageDesc,
  };
  const visibleItems = [...filteredItems].sort(sortFns[sortMode] || sortFns["date-desc"]);

  // ---- Duplicate Cleanup (detection + merge + undo) ---------------------
  // Detector runs against the currently-visible list so users only see
  // groups relevant to what they're looking at. Key: item_name (trimmed +
  // lowercased) | item_data.Vintage | item_data.Region — matches the
  // three highest-signal identity fields in the CDI schema.
  const detectDuplicates = (list) => {
    const map = new Map();
    for (const it of list || []) {
      // Sample cellar items are ephemeral demo data — exclude from
      // merge suggestions so they never surface as duplicates alongside
      // a user's real cellar.
      if (isSampleCollectionId(it.collection_id)) continue;
      const name = (it.item_name || "").trim().toLowerCase();
      if (!name) continue; // skip untitled — they'd all collapse together
      const vintage = String((it.item_data || {}).Vintage || "").trim();
      const region  = String((it.item_data || {}).Region  || "").trim();
      const key = `${name}|${vintage}|${region}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return [...map.values()].filter((g) => g.length > 1);
  };
  const duplicateGroups = detectDuplicates(visibleItems);

  const [dupModalOpen, setDupModalOpen] = useState(false);
  const [dupSaving, setDupSaving] = useState(false);

  // Cleanup history helpers — reuse the same localStorage key format as
  // CategoryBuilder's Field Cleanup History (`cdi:cleanup_history:{catId}`),
  // stored under the master item's category so the record lives with the
  // category it affects. `__uncat__` sentinel for loose items.
  // Cap is user-configurable via Settings → Cleanup History; getter reads
  // localStorage fresh so mid-session changes take effect on the next write.
  const historyKeyFor = (categoryId) => `cdi:cleanup_history:${categoryId || "__uncat__"}`;
  const readHistory = (categoryId) => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(historyKeyFor(categoryId));
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.slice(-getCleanupHistoryCap()) : [];
    } catch { return []; }
  };
  const writeHistory = (categoryId, list) => {
    if (typeof window === "undefined") return;
    try {
      const capped = Array.isArray(list) ? list.slice(-getCleanupHistoryCap()) : [];
      window.localStorage.setItem(historyKeyFor(categoryId), JSON.stringify(capped));
    } catch { /* quota — best effort */ }
  };

  // Compose the merged item body from the master + donors. Master wins
  // on every non-empty field; missing values fill from the first donor
  // that has them.
  const buildMergedItem = (master, group) => {
    const donors = group.filter((it) => it.id !== master.id);
    const pickTop = (key) => {
      if (master[key]) return master[key];
      const donor = donors.find((it) => it[key]);
      return donor ? donor[key] : master[key];
    };
    // Deep-merge item_data key-by-key.
    const mergedData = { ...(master.item_data || {}) };
    for (const d of donors) {
      const dd = d.item_data || {};
      for (const [k, v] of Object.entries(dd)) {
        const empty = mergedData[k] === undefined || mergedData[k] === null || mergedData[k] === "" || (Array.isArray(mergedData[k]) && mergedData[k].length === 0);
        if (empty && v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)) {
          mergedData[k] = v;
        }
      }
    }
    // Only include the top-level scalars the backend PATCH understands.
    return {
      item_name: master.item_name,
      item_data: mergedData,
      image: pickTop("image"),
      images: master.images && master.images.length ? master.images : (donors.find((d) => d.images && d.images.length)?.images || master.images || []),
      fx_rate_purchase: pickTop("fx_rate_purchase"),
      fx_rate_sale: pickTop("fx_rate_sale"),
    };
  };

  const mergeDuplicates = async (master, group) => {
    setDupSaving(true);
    // Snapshot before mutating so Undo can fully restore.
    const preMergeMaster = JSON.parse(JSON.stringify(master));
    const deletedSnapshots = group
      .filter((it) => it.id !== master.id)
      .map((it) => JSON.parse(JSON.stringify(it)));
    let ok = 0, fail = 0;
    try {
      const mergedBody = buildMergedItem(master, group);
      await apiClient.patch(`/items/${master.id}`, mergedBody);
      for (const d of deletedSnapshots) {
        try { await apiClient.delete(`/items/${d.id}`); ok += 1; }
        catch { fail += 1; }
      }
      const record = {
        timestamp: new Date().toISOString(),
        mergedItems: [{
          masterId: master.id,
          groupSize: group.length,
          preMergeMaster,
          deletedSnapshots,
        }],
      };
      const cid = master.category_id || null;
      // Skip history write for sample-cellar merges — sample data is
      // demo state, cleanup history should reflect real user activity.
      if (!isSampleCollectionId(master.collection_id)) {
        const prev = readHistory(cid);
        const next = [...prev, record].slice(-getCleanupHistoryCap());
        writeHistory(cid, next);
      }
      // Digest event — one line per merge action captures how many
      // rows collapsed into which master, for the admin cleanup trail.
      addCleanupEvent({
        type: "merge",
        itemName: master.item_name || "Untitled",
        fromCategory: `${group.length} duplicates`,
        toCategory: catObj(master.category_id)?.category_name || null,
      });

      const toastPos = isMobile ? "bottom-center" : "bottom-right";
      const failMsg = fail > 0 ? ` (${fail} delete${fail === 1 ? "" : "s"} failed)` : "";
      toast.success(`Duplicates merged.${failMsg}`, {
        position: toastPos,
        duration: 8000,
        action: {
          label: "Undo",
          onClick: () => undoLastMerge(cid),
        },
      });
      setDupModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to merge duplicates.");
    } finally {
      setDupSaving(false);
    }
  };

  const undoLastMerge = async (categoryId) => {
    const history = readHistory(categoryId);
    if (history.length === 0) { toast.error("Nothing to undo."); return; }
    const last = history[history.length - 1];
    const entry = last?.mergedItems?.[0];
    if (!entry) { toast.error("Last run is not a merge."); return; }
    let ok = 0, fail = 0;
    try {
      // Restore master to its pre-merge state.
      const pm = entry.preMergeMaster;
      await apiClient.patch(`/items/${pm.id}`, {
        item_name: pm.item_name,
        item_data: pm.item_data || {},
        image: pm.image || "",
        images: pm.images || [],
        fx_rate_purchase: pm.fx_rate_purchase ?? null,
        fx_rate_sale: pm.fx_rate_sale ?? null,
      });
      // Recreate each deleted duplicate (new UUIDs — original ids are lost).
      for (const s of entry.deletedSnapshots || []) {
        try {
          await apiClient.post(`/items`, {
            item_name: s.item_name || "Restored item",
            collection_id: s.collection_id || null,
            category_id: s.category_id || null,
            item_data: s.item_data || {},
            image: s.image || "",
            images: s.images || [],
            fx_rate_purchase: s.fx_rate_purchase ?? null,
            fx_rate_sale: s.fx_rate_sale ?? null,
          });
          ok += 1;
        } catch { fail += 1; }
      }
      // Pop the record from history regardless.
      writeHistory(categoryId, history.slice(0, -1));
      const toastPos = isMobile ? "bottom-center" : "bottom-right";
      if (fail === 0) toast.success("Merge undone.", { position: toastPos });
      else toast.error(`Restored ${ok}, failed ${fail}.`, { position: toastPos });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to undo merge.");
    }
  };

  // For the header label
  const filterCategoryLabel =
    filterCategoryId === UNCAT
      ? "Uncategorised"
      : (catObj(filterCategoryId)?.category_name || "");

  const askDelete = (id) => {
    setRenameId(null);
    setConfirmDeleteId(id);
  };
  const cancelDelete = () => setConfirmDeleteId(null);

  const confirmDelete = async (id) => {
    if (deletingId) return;
    setDeletingId(id);
    try {
      await apiClient.delete(`/items/${id}`);
      toast.success("Item deleted.");
      setConfirmDeleteId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  const itemTitle = (it) =>
    (it.item_data && it.item_data["Item Name"]) || it.name || it.item_name || "Untitled";

  const startRename = (it) => {
    setConfirmDeleteId(null);
    setRenameId(it.id);
    setRenameName(itemTitle(it));
  };
  const cancelRename = () => {
    setRenameId(null);
    setRenameName("");
  };
  const saveRename = async (it) => {
    const trimmed = renameName.trim();
    if (!trimmed) { toast.error("Name is required."); return; }
    setSavingRename(true);
    try {
      const newData = { ...(it.item_data || {}), "Item Name": trimmed };
      await apiClient.patch(`/items/${it.id}`, {
        name: trimmed,
        item_name: trimmed,
        item_data: newData,
      });
      toast.success("Item renamed.");
      setRenameId(null);
      setRenameName("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed.");
    } finally {
      setSavingRename(false);
    }
  };

  // Quick move-to-category for uncategorised items.
  const [movingId, setMovingId] = useState(null);
  const moveToCategory = async (it, newCatId) => {
    if (!newCatId) return;
    setMovingId(it.id);
    try {
      // Special sentinel: clear the category back to Uncategorised.
      // Requirement (Feb 2026): also clear collection_id so an
      // Uncategorised item is truly loose (not still bound to its
      // former collection). Existing __UNCAT__ sentinel + PATCH route
      // are reused — no new backend routes, no schema changes.
      if (newCatId === "__UNCAT__") {
        await apiClient.patch(`/items/${it.id}`, { category_id: null, collection_id: null });
        const toastPos = isMobile ? "bottom-center" : "bottom-right";
        toast.success("Item moved to Uncategorised.", { position: toastPos });
        addCleanupEvent({
          type: "move",
          itemName: it.item_name || "Untitled",
          fromCategory: catObj(it.category_id)?.category_name || "Uncategorised",
          toCategory: "Uncategorised",
        });
        await load();
        return;
      }
      // Also sync collection_id if the chosen category lives in a specific
      // collection (it should — categories require a collection_id).
      const cat = catObj(newCatId);
      const body = { category_id: newCatId };
      if (cat?.collection_id) body.collection_id = cat.collection_id;
      await apiClient.patch(`/items/${it.id}`, body);
      toast.success(`Moved to "${cat?.category_name || "category"}".`);
      // Digest event — differentiate "move" (had a category) vs "assign"
      // (previously uncategorised → first category assignment) so admins
      // can see genuine tidying vs re-shuffles at a glance.
      addCleanupEvent({
        type: it.category_id ? "move" : "assign",
        itemName: it.item_name || "Untitled",
        fromCategory: catObj(it.category_id)?.category_name || "Uncategorised",
        toCategory: cat?.category_name || "—",
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed.");
    } finally {
      setMovingId(null);
    }
  };

  // ---- Bulk selection + Bulk Edit -------------------------------------
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkCollectionId, setBulkCollectionId] = useState("");
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  // Export menu open/close state.
  const [exportOpen, setExportOpen] = useState(false);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const visibleIds = visibleItems.map((i) => i.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const applyBulkEdit = async () => {
    if (selectedIds.size === 0) { toast.error("No items selected."); return; }
    if (!bulkCollectionId && !bulkCategoryId) {
      toast.error("Pick a Collection or Category to apply.");
      return;
    }
    const body = {};
    if (bulkCollectionId) body.collection_id = bulkCollectionId;
    if (bulkCategoryId) {
      body.category_id = bulkCategoryId;
      // Sync collection from the chosen category if the user didn't override.
      const cat = catObj(bulkCategoryId);
      if (cat?.collection_id && !bulkCollectionId) body.collection_id = cat.collection_id;
    }
    setBulkSaving(true);
    let ok = 0, fail = 0;
    // Snapshot pre-move context for the digest so events read naturally
    // ("Moved 3 items to Whisky") without an extra API round-trip.
    const targetCatName = bulkCategoryId ? (catObj(bulkCategoryId)?.category_name || "—") : null;
    const targetColName = bulkCollectionId ? (collections.find((c) => c.id === bulkCollectionId)?.name || "—") : null;
    try {
      // Sequential PATCH — keeps logs clean for small selections.
      for (const id of selectedIds) {
        try { await apiClient.patch(`/items/${id}`, body); ok += 1; }
        catch { fail += 1; }
      }
      if (fail === 0) toast.success(`Updated ${ok} item${ok === 1 ? "" : "s"}.`);
      else toast.error(`Updated ${ok}, failed ${fail}.`);
      // Emit one digest event per updated item so the admin trail
      // stays granular. Skips failed ones so the digest is truthful.
      if (ok > 0) {
        const target = targetCatName || (targetColName ? `${targetColName} (collection)` : "—");
        for (const id of selectedIds) {
          const it = items.find((i) => i.id === id);
          if (!it) continue;
          addCleanupEvent({
            type: it.category_id ? "move" : "assign",
            itemName: it.item_name || "Untitled",
            fromCategory: catObj(it.category_id)?.category_name || "Uncategorised",
            toCategory: target,
          });
        }
      }
      clearSelection();
      setBulkCategoryId(""); setBulkCollectionId("");
      await load();
    } finally {
      setBulkSaving(false);
    }
  };

  // ---- Bulk Move (dedicated category-only modal) ---------------------
  // Lightweight flow: opens MoveItemsModal, collects one category id,
  // and PATCHes each selected item sequentially against the existing
  // /items/{id} endpoint. No new backend routes — pure UI + batch loop.
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveSaving, setMoveSaving] = useState(false);
  const applyBulkMoveToCategory = async (targetCategoryId) => {
    if (!targetCategoryId || selectedIds.size === 0) return;
    setMoveSaving(true);
    const body = {};
    const toUncat = targetCategoryId === "__UNCAT__";
    if (toUncat) {
      // Loose items: clear both category and collection so the item
      // is fully unassigned. Mirrors the per-row __UNCAT__ path.
      body.category_id = null;
      body.collection_id = null;
    } else {
      body.category_id = targetCategoryId;
      const cat = catObj(targetCategoryId);
      if (cat?.collection_id) body.collection_id = cat.collection_id;
    }
    let ok = 0, fail = 0;
    try {
      for (const id of selectedIds) {
        try { await apiClient.patch(`/items/${id}`, body); ok += 1; }
        catch { fail += 1; }
      }
      const toastPos = isMobile ? "bottom-center" : "bottom-right";
      if (fail === 0) {
        const msg = toUncat ? "Items moved to Uncategorised." : "Items moved to category.";
        toast.success(msg, { position: toastPos });
      } else {
        toast.error(`Moved ${ok}, failed ${fail}.`, { position: toastPos });
      }
      clearSelection();
      setMoveModalOpen(false);
      await load();
    } finally {
      setMoveSaving(false);
    }
  };

  // ---- Export ---------------------------------------------------------
  // Created At resolver — items may store the timestamp under several
  // keys depending on the creation path: the API currently serialises
  // it as `date_added`, while legacy / camelCase variants are also
  // possible. Pick the first non-null and format as
  // "YYYY-MM-DD HH:mm:ss" (UTC) so the column is always populated.
  const fmtCreatedAt = (it) => {
    const raw = it.date_added ?? it.created_at ?? it.createdAtUtc ?? it.createdAt;
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  };
  // Build CSV rows from a given items[] array (defaults to the visible/filtered list).
  // Value / profit columns are gated on `isValueInferred` — unvalued items
  // write an empty cell (spreadsheet-friendly) instead of a fabricated $0.
  const itemToRow = (it) => {
    const cat = catObj(it.category_id);
    const col = collections.find((c) => c.id === it.collection_id);
    const cost = itemCost(it);
    const inferred = isValueInferred(it);
    const value = inferred ? null : itemValue(it);
    return {
      name: (it.item_data && it.item_data["Item Name"]) || it.name || it.item_name || "Untitled",
      collection: col?.name || "",
      category: cat?.category_name || "",
      currency: it.currency_code || "NZD",
      cost: cost.toFixed(2),
      est_value: value === null ? "Awaiting Valuation" : value.toFixed(2),
      profit: value === null ? "Awaiting Valuation" : (value - cost).toFixed(2),
      created_at: fmtCreatedAt(it),
    };
  };
  const buildExportRows = (source = visibleItems) => source.map(itemToRow);

  const csvEscape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportCsv = ({ scope = "items" } = {}) => {
    // Export CSV is uncoupled from delete safety rules — always allow
    // the download, including in Preview mode.
    // Determine the source set:
    //   scope='items'      → current visible/filtered list (default behaviour)
    //   scope='collection' → all items in the filtered collection (ignores category filter)
    //                        falls back to all items when no collection is selected.
    const source =
      scope === "collection"
        ? (filterCollectionId
            ? items.filter((it) => it.collection_id === filterCollectionId)
            : items)
        : visibleItems;
    const rows = buildExportRows(source);
    if (rows.length === 0) { toast.error("Nothing to export."); return; }
    // Internal row keys are kept as-is; the CSV header row uses the
    // GST-aliased names so the exported file is self-explanatory.
    const cols = ["name","collection","category","currency","cost","est_value","profit","created_at"];
    const headerAliases = {
      cost: "purchase_price_inc_gst",
      est_value: "value_ex_gst",
      profit: "profit_ex_gst",
    };
    const headerRow = cols.map((c) => headerAliases[c] || c).join(",");
    const lines = [headerRow];
    for (const r of rows) lines.push(cols.map((c) => csvEscape(r[c])).join(","));
    // Adding the BOM helps Excel render UTF-8 correctly when the file is double-clicked.
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const today = new Date().toISOString().slice(0,10);
    let filename;
    if (scope === "collection") {
      const coll = collections.find((c) => c.id === filterCollectionId);
      const safe = (coll?.name || "all-collections").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase();
      filename = `cdi-${safe}-${today}.csv`;
    } else {
      filename = `cdi-items-${today}.csv`;
    }
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Defer URL revoke to give the browser time to start the download in
    // preview iframes (some browsers cancel object URLs revoked too quickly).
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"} → ${filename}`);
  };

  const exportPdf = () => {
    const rows = buildExportRows();
    if (rows.length === 0) { toast.error("Nothing to export."); return; }
    const totalCost = rows.reduce((s, r) => s + Number(r.cost), 0);
    const totalVal = rows.reduce((s, r) => s + Number(r.est_value), 0);
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c]));
    const rowsHtml = rows.map((r) => `
      <tr>
        <td>${esc(r.name)}</td>
        <td>${esc(r.collection)}</td>
        <td>${esc(r.category)}</td>
        <td class="num">${esc(r.cost)} ${esc(r.currency)}</td>
        <td class="num">${esc(r.est_value)} ${esc(r.currency)}</td>
        <td class="num ${Number(r.profit) > 0 ? "pos" : Number(r.profit) < 0 ? "neg" : ""}">${esc(r.profit)}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8">
<title>CDI Items Export</title>
<style>
  body { font-family: -apple-system, system-ui, "Segoe UI", Roboto, sans-serif; padding: 24px; color: #1a1a1a; }
  h1 { margin: 0 0 6px; font-size: 22px; }
  .meta { color: #666; font-size: 12px; margin-bottom: 18px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { padding: 8px 10px; border-bottom: 1px solid #e5e5e5; text-align: left; }
  th { background: #fafafa; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #555; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
  td.pos { color: #1f7a3d; font-weight: 600; }
  td.neg { color: #b91c1c; font-weight: 600; }
  tfoot td { border-top: 2px solid #1a1a1a; border-bottom: none; font-weight: 600; padding-top: 12px; }
  @media print { @page { margin: 14mm 12mm; } }
</style></head><body>
<h1>CDI Collectables — Items Export</h1>
<div class="meta">${rows.length} item${rows.length === 1 ? "" : "s"} · Generated ${new Date().toLocaleString()}</div>
<table><thead><tr>
  <th>Name</th><th>Collection</th><th>Category</th>
  <th style="text-align:right">Purchase Price (Inc GST)</th><th style="text-align:right">Est. Value (Ex GST)</th><th style="text-align:right">P/L (Ex GST)</th>
</tr></thead><tbody>${rowsHtml}</tbody>
<tfoot><tr>
  <td colspan="3">Totals</td>
  <td class="num">${totalCost.toFixed(2)}</td>
  <td class="num">${totalVal.toFixed(2)}</td>
  <td class="num ${(totalVal-totalCost) > 0 ? "pos" : (totalVal-totalCost) < 0 ? "neg" : ""}">${(totalVal-totalCost).toFixed(2)}</td>
</tr></tfoot></table>
<script>window.onload = () => { setTimeout(() => window.print(), 250); };</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Pop-up blocked. Allow pop-ups to export PDF."); return; }
    w.document.open(); w.document.write(html); w.document.close();
    toast.success(`Prepared ${rows.length} item${rows.length === 1 ? "" : "s"} for PDF. Use the browser print dialog.`);
  };

  // Robust guard to prevent first-render fallback
  const safeItems = Array.isArray(items) ? items : [];

  // If helpers failed on first mount, avoid empty-state fallback
  if (!safeItems.length && items && typeof items === "object") {
    // Allow a second render cycle to populate items
    return null;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="items-page">
      <button
        type="button"
        onClick={() => navigate("/collections")}
        data-testid="items-back"
        aria-label="Back to previous screen"
        className="vp-btn vp-btn-secondary vp-btn-sm mb-3 inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <div className="vp-label text-black dark:text-white mb-2">Items</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-black dark:text-white break-words">
            {loading
              ? "…"
              : `${visibleItems.length} item${visibleItems.length === 1 ? "" : "s"}${
                  filterCollectionId ? ` in ${collectionName(filterCollectionId)}` : ""
                }${filterCategoryId ? ` · ${filterCategoryLabel}` : ""}`}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canCsvExport && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setExportOpen((v) => !v)}
              onBlur={() => setTimeout(() => setExportOpen(false), 150)}
              disabled={items.length === 0}
              data-testid="items-data-menu"
              title="Export items or the whole collection"
              className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))] disabled:opacity-50"
            >
              <Download size={14} /> Data
              <ChevronDown size={12} className={`transition-transform duration-fast ${exportOpen ? "rotate-180" : ""}`} />
            </button>
            {exportOpen && (
              <div
                data-testid="items-data-menu-list"
                role="menu"
                className="absolute right-0 top-full mt-1 w-56 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))] shadow-md z-20 overflow-hidden"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => { e.preventDefault(); setExportOpen(false); exportCsv({ scope: "items" }); }}
                  data-testid="items-export-csv"
                  disabled={visibleItems.length === 0}
                  title="Export the currently visible/filtered items as CSV"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--muted))] flex items-center gap-2 disabled:opacity-50"
                >
                  <Download size={13} className="vp-muted" />
                  <span>
                    <span className="block">Export Items (CSV)</span>
                    <span className="block text-[10px] vp-muted">{visibleItems.length} visible row{visibleItems.length === 1 ? "" : "s"}</span>
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => { e.preventDefault(); setExportOpen(false); exportCsv({ scope: "collection" }); }}
                  data-testid="items-export-collection-csv"
                  disabled={items.length === 0}
                  title={filterCollectionId ? "Export every item in the filtered collection" : "Export every item across all collections"}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--muted))] flex items-center gap-2 border-t border-[hsl(var(--border))] disabled:opacity-50"
                >
                  <Download size={13} className="vp-muted" />
                  <span>
                    <span className="block">Export Collection (CSV)</span>
                    <span className="block text-[10px] vp-muted">
                      {filterCollectionId
                        ? `${items.filter((i) => i.collection_id === filterCollectionId).length} row${items.filter((i) => i.collection_id === filterCollectionId).length === 1 ? "" : "s"} in ${collectionName(filterCollectionId)}`
                        : `${items.length} row${items.length === 1 ? "" : "s"} (all)`}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onMouseDown={(e) => { e.preventDefault(); setExportOpen(false); exportPdf(); }}
                  data-testid="items-export-pdf"
                  disabled={visibleItems.length === 0}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--muted))] flex items-center gap-2 border-t border-[hsl(var(--border))] disabled:opacity-50"
                >
                  <Download size={13} className="vp-muted" />
                  <span>
                    <span className="block">Export Items (PDF)</span>
                    <span className="block text-[10px] vp-muted">Opens the print dialog</span>
                  </span>
                </button>
                <Link
                  to="/invoice"
                  role="menuitem"
                  onClick={() => setExportOpen(false)}
                  data-testid="items-import-csv-link"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--muted))] flex items-center gap-2 border-t border-[hsl(var(--border))]"
                >
                  <FileSpreadsheet size={13} className="vp-muted" />
                  <span>
                    <span className="block">Import Invoice…</span>
                    <span className="block text-[10px] vp-muted">PDF or image with line items</span>
                  </span>
                </Link>
              </div>
            )}
          </div>
          )}
          {canCsvExport && (
          <Link
            to="/invoice"
            data-testid="items-import-invoice"
            title="Bulk-add items from an invoice (PDF or image)"
            className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
          >
            <FileSpreadsheet size={14} /> Import Invoice
          </Link>
          )}
          <Link
            to="/items/new"
            data-testid="items-create"
            className="vp-btn vp-btn-primary"
          >
            <Plus size={16} /> Create Item
          </Link>
        </div>
      </div>

      {/* Search bar — filters items live as the user types across:
          Item Name, Producer, Region, Sub-Region, Grape(s), Vintage, Notes.
          Wrapped with a leading magnifying-glass icon + stronger border so
          it's visually obvious in both light and dark mode. */}
      <div className="mb-4 relative">
        <Search
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
        />
        <div className="[&_input]:!pl-9 [&_button[role=combobox]]:!pl-9 [&_button[role=combobox]]:!border-[hsl(var(--border-strong))] [&_input]:!border-[hsl(var(--border-strong))]">
          <ComboBox
            testid="items-search"
            value={searchInput}
            onChange={setSearchInput}
            options={itemNameOptions}
            placeholder="Search items…"
            maxResults={10}
            allowAdd
            hideAddOption
          />
        </div>
      </div>

      {/* Filters toggle + Sort row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          data-testid="items-filters-toggle"
          aria-expanded={filtersOpen}
          aria-controls="items-filters-panel"
          className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
        >
          <Filter size={14} />
          <span className="text-xs font-medium">Filters</span>
          {activeFilterCount > 0 && (
            <span
              data-testid="items-filters-active-count"
              className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--gold-strong))] dark:bg-[hsl(var(--gold))] text-[10px] font-semibold text-white"
            >
              {activeFilterCount}
            </span>
          )}
          <ChevronDown size={12} className={`transition-transform duration-fast ${filtersOpen ? "rotate-180" : ""}`} />
        </button>
        <div className="flex items-center gap-2 ml-auto">
          <label htmlFor="items-sort" className="text-xs font-medium text-black dark:text-white">Sort</label>
          <div className="w-52">
            <ComboBox
              id="items-sort"
              testid="items-sort"
              value={sortMode}
              onChange={(v) => setSortMode(v || "date-desc")}
              allowAdd={false}
              options={[
                { value: "date-desc", label: "Date Added (newest first)" },
                { value: "date-asc", label: "Date Added (oldest first)" },
                { value: "vintage-desc", label: "Vintage (newest first)" },
                { value: "vintage-asc", label: "Vintage (oldest first)" },
                { value: "name-asc", label: "Name (A→Z)" },
                { value: "name-desc", label: "Name (Z→A)" },
                { value: "price-asc", label: "Price (low→high)" },
                { value: "price-desc", label: "Price (high→low)" },
                { value: "value-asc", label: "Value (low→high)" },
                { value: "value-desc", label: "Value (high→low)" },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Collapsible Filters panel. CSS-grid-rows transition gives a smooth
          slide effect without measuring heights in JS.                       */}
      <div
        id="items-filters-panel"
        data-testid="items-filters-panel"
        aria-hidden={!filtersOpen}
        className={`grid transition-all duration-300 ease-out overflow-hidden ${
          filtersOpen ? "grid-rows-[1fr] opacity-100 mb-8" : "grid-rows-[0fr] opacity-0 mb-0"
        }`}
      >
        <div className="min-h-0">
          <div className="vp-card p-5">
            {/* Global filter group — Collection + Category. Always visible;
                selecting a Collection determines which type-specific group
                renders in the block below. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
              <FilterCombo
                label="Collection"
                testid="items-filter-collection"
                value={filterCollectionId}
                onChange={setFilterCollectionId}
                options={[{ value: "", label: "All collections" }, ...collections.map((c) => ({ value: c.id, label: c.name }))]}
              />
              <FilterCombo
                label="Category"
                testid="items-filter-category"
                value={filterCategoryId}
                onChange={setFilterCategoryId}
                options={[
                  { value: "", label: "All categories" },
                  { value: UNCAT, label: "— Uncategorised —" },
                  ...filteredCategoryOptions.map((c) => ({ value: c.id, label: c.category_name })),
                ]}
              />
            </div>

            {/* Type-aware filter group — locked schemas per collection
                type. The active group is decided by `activeCollectionType`,
                which reads collections[].type_id. Wine remains the
                fallback when no collection is picked yet so the panel
                doesn't collapse to an empty shell on first load. */}
            <div className="mt-5 pt-5 border-t border-[hsl(var(--border))]" data-testid={`items-filter-group-${activeCollectionType || "wine"}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-semibold uppercase tracking-wider vp-muted">
                  {activeCollectionType === "whisky"  ? "Whisky filters" :
                   activeCollectionType === "spirits" ? "Spirits filters" :
                   activeCollectionType === "general" ? "General Collectables filters" :
                   activeCollectionType === "custom"  ? "Custom filters" :
                   "Wine filters"}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
                {activeCollectionType === "whisky" && (
                  <>
                    <FilterCombo label="Distillery" testid="items-filter-distillery" value={filterDistillery} onChange={setFilterDistillery}
                      options={[{ value: "", label: "All distilleries" }, ...filterPresets.Distillery.map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Cask Type" testid="items-filter-cask-type" value={filterCaskType} onChange={setFilterCaskType}
                      options={[{ value: "", label: "All cask types" }, ...filterPresets["Cask Type"].map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Region" testid="items-filter-region" value={filterRegion} onChange={setFilterRegion}
                      options={[{ value: "", label: "All regions" }, ...filterPresets.Region.map((v) => ({ value: v, label: v }))]} />
                    <FilterRange label="Age" testid="items-filter-age" min={filterAgeMin} max={filterAgeMax}
                      onChangeMin={(v) => setFilter("age_min", v)} onChangeMax={(v) => setFilter("age_max", v)}
                      placeholderMin="from" placeholderMax="to" />
                    <FilterRange label="ABV" testid="items-filter-abv" min={filterAbvMin} max={filterAbvMax}
                      onChangeMin={(v) => setFilter("abv_min", v)} onChangeMax={(v) => setFilter("abv_max", v)}
                      placeholderMin="from" placeholderMax="to" />
                  </>
                )}

                {activeCollectionType === "spirits" && (
                  <>
                    <FilterCombo label="Type" testid="items-filter-spirit-type" value={filterSpiritType} onChange={setFilterSpiritType}
                      options={[{ value: "", label: "All types" }, ...filterPresets.Type.map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Country" testid="items-filter-country" value={filterCountry} onChange={setFilterCountry}
                      options={[{ value: "", label: "All countries" }, ...filterPresets.Country.map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Region" testid="items-filter-region" value={filterRegion} onChange={setFilterRegion}
                      options={[{ value: "", label: "All regions" }, ...filterPresets.Region.map((v) => ({ value: v, label: v }))]} />
                    <FilterRange label="ABV" testid="items-filter-abv" min={filterAbvMin} max={filterAbvMax}
                      onChangeMin={(v) => setFilter("abv_min", v)} onChangeMax={(v) => setFilter("abv_max", v)}
                      placeholderMin="from" placeholderMax="to" />
                  </>
                )}

                {activeCollectionType === "general" && (
                  <>
                    <FilterCombo label="Category" testid="items-filter-gc-category" value={filterGcCategory} onChange={setFilterGcCategory}
                      options={[{ value: "", label: "All categories" }, ...filterPresets.Category.map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Condition" testid="items-filter-condition" value={filterCondition} onChange={setFilterCondition}
                      options={[{ value: "", label: "All conditions" }, ...filterPresets.Condition.map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Grade / Certification" testid="items-filter-grade" value={filterGrade} onChange={setFilterGrade}
                      options={[{ value: "", label: "All grades" }, ...filterPresets["Grade / Certification"].map((v) => ({ value: v, label: v }))]} />
                  </>
                )}

                {activeCollectionType === "custom" && (
                  <>
                    <FilterCombo label="Primary Identifier" testid="items-filter-primary-id" value={filterPrimaryId} onChange={setFilterPrimaryId}
                      options={[{ value: "", label: "All" }, ...filterPresets["Primary Identifier"].map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Secondary Identifier" testid="items-filter-secondary-id" value={filterSecondaryId} onChange={setFilterSecondaryId}
                      options={[{ value: "", label: "All" }, ...filterPresets["Secondary Identifier"].map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Condition / Grade" testid="items-filter-condition-grade" value={filterConditionGrade} onChange={setFilterConditionGrade}
                      options={[{ value: "", label: "All" }, ...filterPresets["Condition / Grade"].map((v) => ({ value: v, label: v }))]} />
                  </>
                )}

                {(activeCollectionType === "wine" || activeCollectionType === "") && (
                  <>
                    <FilterCombo label="Producer" testid="items-filter-producer" value={filterProducer} onChange={setFilterProducer}
                      options={[{ value: "", label: "All producers" }, ...filterPresets.Producer.map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Region" testid="items-filter-region" value={filterRegion} onChange={setFilterRegion}
                      options={[{ value: "", label: "All regions" }, ...filterPresets.Region.map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Sub-Region" testid="items-filter-sub-region" value={filterSubRegion} onChange={setFilterSubRegion}
                      options={[{ value: "", label: "All sub-regions" }, ...filterPresets["Sub-Region"].map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Grape(s)" testid="items-filter-grape" value={filterGrape} onChange={setFilterGrape}
                      options={[{ value: "", label: "All grapes" }, ...grapeOptions.map((v) => ({ value: v, label: v }))]} />
                    <FilterCombo label="Bottle Size" testid="items-filter-bottle-size" value={filterBottleSize} onChange={setFilterBottleSize}
                      options={[{ value: "", label: "All sizes" }, ...bottleSizeOptions.map((s) => ({ value: s, label: s }))]} />
                    <FilterRange label="Vintage" testid="items-filter-vintage" min={filterVintageMin} max={filterVintageMax}
                      onChangeMin={(v) => setFilter("vmin", v)} onChangeMax={(v) => setFilter("vmax", v)}
                      placeholderMin="e.g. 2018" placeholderMax="e.g. 2023" />
                    <FilterRange label="Rating" testid="items-filter-rating" min={filterRatingMin} max={filterRatingMax}
                      onChangeMin={(v) => setFilter("rmin", v)} onChangeMax={(v) => setFilter("rmax", v)}
                      placeholderMin="0" placeholderMax="100" />
                    <FilterRange label="Drinking Window — Start" testid="items-filter-dw-start" min={filterDwStartMin} max={filterDwStartMax}
                      onChangeMin={(v) => setFilter("dwsmin", v)} onChangeMax={(v) => setFilter("dwsmax", v)}
                      placeholderMin="from" placeholderMax="to" />
                    <FilterRange label="Drinking Window — Peak" testid="items-filter-dw-peak" min={filterDwPeakMin} max={filterDwPeakMax}
                      onChangeMin={(v) => setFilter("dwpmin", v)} onChangeMax={(v) => setFilter("dwpmax", v)}
                      placeholderMin="from" placeholderMax="to" />
                    <FilterRange label="Drinking Window — End" testid="items-filter-dw-end" min={filterDwEndMin} max={filterDwEndMax}
                      onChangeMin={(v) => setFilter("dwemin", v)} onChangeMax={(v) => setFilter("dwemax", v)}
                      placeholderMin="from" placeholderMax="to" />
                  </>
                )}
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between flex-wrap gap-2">
              <span className="text-[11px] vp-muted">
                {activeFilterCount > 0
                  ? `${activeFilterCount} active filter${activeFilterCount === 1 ? "" : "s"} · ${visibleItems.length} item${visibleItems.length === 1 ? "" : "s"} match`
                  : "No filters active — showing all items."}
              </span>
              <button
                type="button"
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
                data-testid="items-filter-clear"
                className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))] disabled:opacity-40"
              >
                Clear all filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <SkeletonList rows={5} />
      ) : visibleItems.length === 0 ? (
        safeItems.length === 0 ? (
          <EmptyState
            Icon={Wine}
            title="No items in this category"
            body="Add your first item to begin building your collection."
            actionLabel="Add item"
            actionTo="/items/new"
            testid="items-empty"
          />
        ) : (
          <EmptyState
            Icon={Search}
            title="No results found"
            body="Try adjusting your search or filters."
            testid="items-empty-search"
          />
        )
      ) : (
        <>
        {selectedIds.size > 0 && (
          <div
            data-testid="items-bulk-toolbar"
            className="vp-card p-4 mb-3 flex items-center gap-3 flex-wrap sticky top-2 z-10 border-[hsl(var(--gold))]"
          >
            <span className="text-sm font-medium">
              {selectedIds.size} item{selectedIds.size === 1 ? "" : "s"} selected
            </span>
            <span className="vp-muted text-xs">·</span>
            <label className="text-[10px] uppercase tracking-wider vp-muted">Move to</label>
            <select
              data-testid="bulk-collection"
              value={bulkCollectionId}
              onChange={(e) => {
                setBulkCollectionId(e.target.value);
                if (bulkCategoryId && catObj(bulkCategoryId)?.collection_id !== e.target.value) {
                  setBulkCategoryId("");
                }
              }}
              className="vp-select vp-select-sm h-8 !py-1 !pl-3 !pr-8 leading-tight text-sm"
            >
              <option value="">— Collection —</option>
              {collections.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
            <select
              data-testid="bulk-category"
              value={bulkCategoryId}
              onChange={(e) => {
                setBulkCategoryId(e.target.value);
                const cat = catObj(e.target.value);
                if (cat?.collection_id && !bulkCollectionId) setBulkCollectionId(cat.collection_id);
              }}
              className="vp-select vp-select-sm h-8 !py-1 !pl-3 !pr-8 leading-tight text-sm"
            >
              <option value="">— Category —</option>
              {categories
                .filter((c) => !bulkCollectionId || c.collection_id === bulkCollectionId)
                .map((c) => (<option key={c.id} value={c.id}>{c.category_name}</option>))}
            </select>
            <button
              type="button"
              onClick={applyBulkEdit}
              disabled={bulkSaving || (!bulkCollectionId && !bulkCategoryId)}
              data-testid="bulk-apply"
              className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50 ml-auto"
            >
              {bulkSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {bulkSaving ? "Applying…" : "Apply"}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              data-testid="bulk-cancel"
              className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
            >
              <X size={14} /> Clear
            </button>
          </div>
        )}
        {duplicateGroups.length > 0 && (
          <div
            className="mb-4 rounded-md border border-yellow-400/60 bg-yellow-50 dark:bg-yellow-500/10 text-yellow-900 dark:text-yellow-100 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid="items-dup-banner"
            role="status"
          >
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle size={16} className="shrink-0" />
              <span className="text-sm">
                <strong className="font-semibold">{duplicateGroups.length}</strong> duplicate group{duplicateGroups.length === 1 ? "" : "s"} found in the current view.
              </span>
            </div>
            <button
              type="button"
              onClick={() => setDupModalOpen(true)}
              data-testid="items-dup-review"
              className="vp-btn vp-btn-secondary vp-btn-sm"
            >
              <Copy size={13} /> Review &amp; Merge
            </button>
          </div>
        )}
        <ul className="vp-card overflow-hidden vp-stagger" data-testid="items-list">
          <li className="px-5 sm:px-6 py-3 border-b border-[hsl(var(--border))] flex items-center gap-3 bg-[hsl(var(--muted))]">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAllVisible}
              data-testid="items-select-all"
              aria-label="Select all visible items"
              className="vp-check"
            />
            <span className="text-xs vp-muted">
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : `Select to bulk edit (${visibleItems.length} shown)`}
            </span>
            <button
              type="button"
              onClick={() => setMoveModalOpen(true)}
              disabled={selectedIds.size === 0}
              data-testid="items-bulk-move"
              title={selectedIds.size === 0
                ? "Select at least one item to move"
                : `Move ${selectedIds.size} selected item${selectedIds.size === 1 ? "" : "s"} to a category`}
              aria-label="Move selected items to a category"
              className="ml-auto vp-btn vp-btn-secondary vp-btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowRightLeft size={13} />
              <span className="hidden sm:inline">Move selected items</span>
              <span className="sm:hidden">Move…</span>
            </button>
          </li>
          {visibleItems.map((it) => (
            <li
              key={it.id}
              data-testid={`item-row-${it.id}`}
              className={`vp-table-row border-b border-[hsl(var(--border))] last:border-b-0 ${selectedIds.has(it.id) ? "bg-[hsl(var(--muted))]" : ""}`}
            >
              <div className="flex items-center gap-4 px-5 sm:px-6 py-5">
              <input
                type="checkbox"
                checked={selectedIds.has(it.id)}
                onChange={() => toggleSelected(it.id)}
                data-testid={`item-select-${it.id}`}
                aria-label="Select item"
                className="vp-check shrink-0"
              />
              <div className="w-11 h-11 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] overflow-hidden shrink-0">
                {(it.image || it.image_url) && (
                  <img src={it.image || it.image_url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              {renameId === it.id ? (
                <div className="flex-1 min-w-0 flex items-center gap-2" data-testid={`item-rename-form-${it.id}`}>
                  <input
                    type="text"
                    autoFocus
                    value={renameName}
                    onChange={(e) => setRenameName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveRename(it);
                      else if (e.key === "Escape") cancelRename();
                    }}
                    data-testid={`item-rename-input-${it.id}`}
                    placeholder="Item name"
                    className="vp-input flex-1 min-w-0 h-9"
                  />
                </div>
              ) : (
                <Link
                  to={`/items/${it.id}`}
                  state={{ from: location.pathname + location.search }}
                  data-testid={`item-link-${it.id}`}
                  className="flex-1 min-w-0 hover:opacity-90"
                >
                  {/* Wine name + vintage — on mobile allow up to 2 lines of
                      wrapping so long names like "Domaine Anne Gros
                      Bourgogne Pinot Noir 2020" aren't ellipsised at the
                      right (and never get clipped on the left because the
                      Link itself is the flex item). Desktop keeps the
                      single-line truncate so list density stays the same. */}
                  {/* Title block — for the Wine collection (when the user
                      has NOT overridden the auto-built name) we surface a
                      structured 2-line view: Producer on top, then
                      "Cuvée · Classification · Vintage" beneath. Falls
                      back to the existing single-line `itemTitle` for
                      every other case (non-Wine collection, manually
                      overridden name, or legacy items missing structured
                      pieces). */}
                  {(() => {
                    const col = collections.find((c) => c.id === it.collection_id);
                    const isWine =
                      (col?.type_id === "wine") ||
                      (!col?.type_id && /wine/i.test(col?.name || ""));
                    const isSpirits =
                      !isWine && (
                        (col?.type_id === "spirits") ||
                        /(whisky|whiskey|rum|tequila|cognac|gin|vodka|mezcal|armagnac|liqueur)/i.test(col?.name || "")
                      );
                    const override = !!(it.item_data || {})["Name Override"];
                    if (isWine && !override) {
                      const producer = String((it.item_data || {})["Producer"] || "").trim();
                      const cuvee = String((it.item_data || {})["Cuvée"] || "").trim();
                      const classification = String((it.item_data || {})["Classification"] || "").trim();
                      const vintage = String((it.item_data || {})["Vintage"] || "").trim();
                      if (producer || cuvee) {
                        const secondary = [cuvee, classification, vintage].filter(Boolean).join(" · ");
                        return (
                          <div data-testid={`item-row-structured-${it.id}`} className="min-w-0 max-w-full pr-2">
                            {producer && (
                              <div className="text-base font-medium tracking-tight break-words [overflow-wrap:anywhere] line-clamp-2 sm:line-clamp-2 max-w-full">
                                {producer}
                              </div>
                            )}
                            {secondary && (
                              <div className="text-xs vp-muted mt-0.5 break-words [overflow-wrap:anywhere] line-clamp-2 sm:line-clamp-2 max-w-full">
                                {secondary}
                              </div>
                            )}
                          </div>
                        );
                      }
                    }
                    if (isSpirits && !override) {
                      const brand = String((it.item_data || {})["Brand"] || "").trim();
                      const product = String((it.item_data || {})["Product"] || "").trim();
                      const region = String((it.item_data || {})["Region"] || "").trim();
                      if (brand || product) {
                        const secondary = [product, region].filter(Boolean).join(" · ");
                        return (
                          <div data-testid={`item-row-structured-spirits-${it.id}`} className="min-w-0 max-w-full pr-2">
                            {brand && (
                              <div className="text-base font-medium tracking-tight break-words [overflow-wrap:anywhere] line-clamp-2 sm:line-clamp-2 max-w-full">
                                {brand}
                              </div>
                            )}
                            {secondary && (
                              <div className="text-xs vp-muted mt-0.5 break-words [overflow-wrap:anywhere] line-clamp-2 sm:line-clamp-2 max-w-full">
                                {secondary}
                              </div>
                            )}
                          </div>
                        );
                      }
                    }
                    return (
                      <div className="text-base font-medium tracking-tight break-words [overflow-wrap:anywhere] line-clamp-2 sm:line-clamp-2 max-w-full pr-2">
                        {itemTitle(it)}
                      </div>
                    );
                  })()}
                  {/* Secondary identity line (metadata reconnect):
                      • `Producer · Appellation · Vintage` for Wine,
                        `Distillery · Age · Cask Type` for Whisky,
                        `Brand / Producer · Spirit Type` for Spirits.
                      • MANUAL pill (gold) rendered when the item's
                        `Name Override` flag is on.
                      Primary title above is unchanged — kept per
                      wiring-preservation rule. Uses the shared
                      composeIdentityDots + isNameOverride helpers so
                      Home Continue pill + ItemEntry preview stay in
                      lock-step. */}
                  {(() => {
                    const col = collections.find((c) => c.id === it.collection_id);
                    const identity = composeIdentityDots(it.item_data, col);
                    const override = isNameOverride(it.item_data);
                    if (!identity && !override) return null;
                    return (
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap min-w-0">
                        {override ? (
                          <span
                            data-testid={`cb-items-manual-pill-${it.id}`}
                            title="Item Name is manually overridden."
                            className="text-[10px] uppercase tracking-wider border border-[hsl(var(--gold))] text-[hsl(var(--gold))] rounded-full px-1.5 py-0.5"
                          >
                            Manual
                          </span>
                        ) : null}
                        {identity ? (
                          <span
                            data-testid={`cb-items-composed-name-${it.id}`}
                            className="text-xs vp-muted break-words [overflow-wrap:anywhere] line-clamp-1 min-w-0"
                          >
                            {identity}
                          </span>
                        ) : null}
                      </div>
                    );
                  })()}
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  {/* Region pill — sourced from item_data.Region (NOT
                      Category). Rendered BEFORE the CategoryPill per
                      the metadata-wiring fix so cellars where the
                      Region field is populated ("South Australia", etc.)
                      surface the actual item region rather than the
                      category label. Hidden when the value is blank.
                      Styling mirrors the neighbouring pills (same
                      padding / radius / border / background) so the row
                      stays visually consistent. */}
                  {(() => {
                    const region = String((it.item_data || {})["Region"] || "").trim();
                    if (!region) return null;
                    return (
                      <span
                        data-testid={`item-region-pill-${it.id}`}
                        title="Region"
                        className="inline-flex items-center px-2 py-1 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-xs font-medium text-[hsl(var(--text))] max-w-full whitespace-nowrap"
                      >
                        {region}
                      </span>
                    );
                  })()}
                  <CategoryPill category={catObj(it.category_id)} muted />
                  {/* Read-only Type pill — surfaces item_data["Type"] (the
                      canonical Wine type field) next to the Region/Category
                      chip so the list is scannable at a glance. Hidden when
                      blank so non-Wine items stay clean. Style mirrors the
                      Region/Category pill (same padding, radius, border,
                      background) for visual consistency — distinguished
                      only by a small gold dot + gold text. Legacy items
                      keyed under "Wine Type" still resolve. */}
                  {(() => {
                    const t = ((it.item_data || {})["Type"] || (it.item_data || {})["Wine Type"] || "").toString().trim();
                    if (!t) return null;
                    return (
                      <span
                        data-testid={`item-type-pill-${it.id}`}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-xs font-medium text-[hsl(var(--text))] max-w-full whitespace-nowrap"
                        title={`Type: ${t}`}
                      >
                        <span
                          aria-hidden="true"
                          className="h-2 w-2 rounded-full bg-[hsl(var(--gold-strong))] dark:bg-[hsl(var(--gold))] shrink-0"
                        />
                        <span className="truncate">{t}</span>
                      </span>
                    );
                  })()}
                  {/* Case Lot badge — small visual indicator for items that
                      ship as a sealed Full Case or a Partial Case. Single
                      Bottles (the default) renders nothing (existing
                      behaviour). Styled to mirror the Type pill above so
                      the row stays visually consistent — distinguished by
                      a tiny Package icon. */}
                  {(() => {
                    const ct = ((it.item_data || {})["Case Type"] || "").toString().trim();
                    if (!ct || ct === "Single Bottles") return null;
                    const label = ct === "Full Case" ? "Full Case" : ct === "Partial Case" ? "Partial Case" : ct;
                    return (
                      <span
                        data-testid={`item-case-pill-${it.id}`}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-xs font-medium text-[hsl(var(--text))] max-w-full whitespace-nowrap"
                        title={`Case: ${label}`}
                      >
                        <Package size={11} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] shrink-0" />
                        <span className="truncate">{label}</span>
                      </span>
                    );
                  })()}
                  {/* Bottle Size pill — always visible when Bottle Size is set
                      in item_data, independent of the composed identity
                      subtitle. Guarantees the size is never clipped by
                      identity clamping/truncation. Rendered on a stable
                      metadata row (flex-wrap) so it drops to the next line
                      cleanly when the row overflows. Consistent behaviour
                      across Wine, Spirits, and Whisky (all read the same
                      item_data["Bottle Size"] key). */}
                  {(() => {
                    const bs = ((it.item_data || {})["Bottle Size"] || "").toString().trim();
                    if (!bs) return null;
                    return (
                      <span
                        data-testid={`item-bottle-size-pill-${it.id}`}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-xs font-medium text-[hsl(var(--text))] max-w-full whitespace-nowrap shrink-0"
                        title={`Bottle Size: ${bs}`}
                      >
                        <span className="text-[10px] uppercase tracking-wider vp-muted shrink-0">Size</span>
                        <span className="truncate">{bs}</span>
                      </span>
                    );
                  })()}
                  {/* Category reassignment dropdown — available for ALL items
                      (was previously gated to uncategorised only). Clicking
                      the chevron does not propagate to the row link.
                      Mobile: remove the max-w cap so the trigger isn't
                      cramped to ~180 px and so the OS-native option list
                      opens with the full option labels visible. Desktop
                      retains the tighter sm:max-w cap.
                      Uses `vp-select-strong` for CDI-gold chevron + crisper
                      border + hover/focus states so the dropdown affordance
                  {/* Category move dropdown. Sits inside the row's <Link>,
                      so we spread `linkSafeSelectHandlers` — see
                      `lib/dropdownHandlers.js` for the split-responsibility
                      rationale (mousedown opens the native dropdown, click
                      cancels the anchor navigation). */}
                  <select
                    data-testid={`item-move-${it.id}`}
                    value=""
                    disabled={movingId === it.id}
                    {...linkSafeSelectHandlers((val) => moveToCategory(it, val))}
                    className="vp-select vp-select-sm vp-select-strong text-[11px] font-medium h-8 sm:h-7 py-0 pl-3 pr-8 w-full basis-full sm:basis-auto sm:w-auto sm:min-w-[160px] sm:max-w-[220px] text-[hsl(var(--text))] disabled:opacity-50"
                    title={it.category_id ? "Change category" : "Move to a category"}
                    aria-label={it.category_id ? "Change category" : "Move to a category"}
                  >
                    <option value="" disabled>
                      {movingId === it.id
                        ? "Moving…"
                        : (it.category_id
                            ? (isMobile ? "Change…" : "Change category…")
                            : (isMobile ? "Move…" : "Move to category…"))}
                    </option>
                    {/* Category filter — spec-compliant:
                        • When the item has a collection, list EVERY category
                          in that collection (no type/tag/template inference).
                        • When the item has NO collection (fully loose), list
                          every category in the app so the user can still
                          pick a home.
                        • Exclude only the item's CURRENT category so the
                          menu never offers a no-op. */}
                    {categories
                      .filter((c) => (it.collection_id ? c.collection_id === it.collection_id : true))
                      .filter((c) => c.id !== it.category_id)
                      .map((c) => (
                        <option key={c.id} value={c.id}>{c.category_name}</option>
                      ))}
                    {it.category_id && (
                      <option
                        value="__UNCAT__"
                        className="italic text-gray-500 border-t border-gray-300 mt-1 pt-1"
                      >
                        {isMobile ? "Uncategorised" : "— Uncategorised —"}
                      </option>
                    )}
                  </select>
                  {it.collection_id && (
                    <span
                      role="link"
                      tabIndex={0}
                      data-testid={`item-collection-badge-${it.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/collections/${it.collection_id}`);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/collections/${it.collection_id}`);
                        }
                      }}
                      className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] cursor-pointer transition-colors duration-fast"
                      title="Open collection"
                    >
                      {collectionName(it.collection_id)}
                    </span>
                  )}
                </div>
              </Link>
              )}
              <div
                className="hidden sm:flex flex-col items-end text-xs font-mono shrink-0 mr-1 gap-y-0.5 tabular-nums"
                data-testid={`item-price-${it.id}`}
              >
                <span className="vp-muted">
                  Purchase Price <span className="text-[9px] tracking-wider uppercase">(Inc GST)</span>
                </span>
                <span data-testid={`item-cost-${it.id}`} className="font-medium inline-flex items-baseline gap-2 whitespace-nowrap">
                  {fmtPrice(itemCost(it))}
                  <span
                    data-testid={`item-currency-${it.id}`}
                    className="text-[9px] font-sans font-medium tracking-wider uppercase px-1.5 py-0.5 rounded border border-[hsl(var(--border))] vp-muted leading-none"
                  >
                    {it.currency_code || "NZD"}
                  </span>
                </span>
                <span className="vp-muted mt-1.5">
                  Est. Value <span className="text-[9px] tracking-wider uppercase">(Ex GST)</span>
                </span>
                <InferredValueIndicator inferred={isValueInferred(it)}>
                  <span
                    data-testid={`item-est-${it.id}`}
                    className="font-medium inline-flex items-baseline gap-2 whitespace-nowrap text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
                  >
                    {isValueInferred(it) ? (
                      <span className="vp-muted italic">Awaiting Valuation</span>
                    ) : (
                      <>
                        {fmtPrice(itemValue(it))}
                        <span
                          className="text-[9px] font-sans font-medium tracking-wider uppercase px-1.5 py-0.5 rounded border border-[hsl(var(--border))] vp-muted leading-none"
                        >
                          {it.currency_code || "NZD"}
                        </span>
                      </>
                    )}
                  </span>
                </InferredValueIndicator>
              </div>
              {renameId === it.id ? (
                <div className="flex items-center gap-1" data-testid={`item-rename-actions-${it.id}`}>
                  <button
                    type="button"
                    onClick={() => saveRename(it)}
                    disabled={savingRename || !renameName.trim()}
                    data-testid={`item-rename-save-${it.id}`}
                    aria-label="Save rename"
                    className="vp-btn vp-btn-sm !h-9 !w-9 !p-0 bg-[hsl(var(--gold))] text-[#1a1a1a] hover:opacity-90 disabled:opacity-50"
                  >
                    {savingRename ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={cancelRename}
                    data-testid={`item-rename-cancel-${it.id}`}
                    aria-label="Cancel rename"
                    className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 border border-[hsl(var(--border))]"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : confirmDeleteId === it.id ? (
                <div
                  className="flex items-center gap-1 bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-md p-1 shadow-sm"
                  data-testid={`item-delete-confirm-${it.id}`}
                >
                  <button
                    type="button"
                    onClick={() => confirmDelete(it.id)}
                    disabled={deletingId === it.id}
                    data-testid={`item-delete-yes-${it.id}`}
                    aria-label="Confirm delete item"
                    className="vp-btn vp-btn-sm !h-8 !px-2 bg-[hsl(var(--loss))] text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {deletingId === it.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    <span className="text-xs font-medium">Delete</span>
                  </button>
                  <button
                    type="button"
                    onClick={cancelDelete}
                    data-testid={`item-delete-cancel-${it.id}`}
                    aria-label="Cancel delete"
                    className="vp-btn vp-btn-ghost vp-btn-sm !h-8 !w-8 !p-0 border border-[hsl(var(--border))]"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <Link
                    to={`/items/${it.id}/edit`}
                    state={{ from: location.pathname + location.search }}
                    data-testid={`item-edit-${it.id}`}
                    aria-label="Edit item"
                    title="Edit item"
                    className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 border border-[hsl(var(--border))]"
                  >
                    <Edit2 size={14} />
                  </Link>
                  <button
                    onClick={() => askDelete(it.id)}
                    data-testid={`item-delete-${it.id}`}
                    aria-label="Delete item"
                    title="Delete item"
                    className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 border border-[hsl(var(--loss))] text-[hsl(var(--loss))]"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
              </div>
            </li>
          ))}
        </ul>
        </>
      )}

      <BottomNav current="items" />

      <MoveItemsModal
        open={moveModalOpen}
        onClose={() => { if (!moveSaving) setMoveModalOpen(false); }}
        onConfirm={applyBulkMoveToCategory}
        selectedCount={selectedIds.size}
        categories={categories}
        isMobile={isMobile}
        saving={moveSaving}
      />

      <DuplicateCleanupModal
        open={dupModalOpen}
        onClose={() => { if (!dupSaving) setDupModalOpen(false); }}
        groups={duplicateGroups}
        onMerge={mergeDuplicates}
        saving={dupSaving}
      />
    </div>
  );
}


/* ------------------------------------------------------------------
   FilterCombo — labelled ComboBox tile used inside the Filters panel.
   Provides a stacked layout (label above, control below) so multi-column
   grid rows stay aligned regardless of label length.
   ------------------------------------------------------------------ */
function FilterCombo({ label, testid, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={testid} className="text-[11px] font-medium uppercase tracking-wider vp-muted">
        {label}
      </label>
      <ComboBox
        id={testid}
        testid={testid}
        value={value}
        onChange={(v) => onChange(v || "")}
        allowAdd={false}
        options={options}
      />
    </div>
  );
}

/* ------------------------------------------------------------------
   FilterRange — numeric min/max pair for range filters (Vintage, Rating,
   Drinking Window Start/Peak/End). Either bound is optional; empty means
   unbounded on that side.
   ------------------------------------------------------------------ */
function FilterRange({ label, testid, min, max, onChangeMin, onChangeMax, placeholderMin, placeholderMax }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wider vp-muted">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          inputMode="numeric"
          value={min}
          onChange={(e) => onChangeMin(e.target.value)}
          placeholder={placeholderMin || "min"}
          data-testid={`${testid}-min`}
          aria-label={`${label} minimum`}
          className="vp-input flex-1 min-w-0"
        />
        <span className="vp-muted text-xs select-none">–</span>
        <input
          type="number"
          inputMode="numeric"
          value={max}
          onChange={(e) => onChangeMax(e.target.value)}
          placeholder={placeholderMax || "max"}
          data-testid={`${testid}-max`}
          aria-label={`${label} maximum`}
          className="vp-input flex-1 min-w-0"
        />
      </div>
    </div>
  );
}
